<template>

  <div class="ob-content">
    <ob-filters>
      <div class="filters">
        <assets-tree v-model="assetIds" :shv-disabled="true" @assets="assets = $event"></assets-tree>

        <variables-tree v-model="variableIds" :assets="assets" :autoselect="true" @variables="leafVariableIds = $event"></variables-tree>

        <v-text-field :label="$t('device-statistics.limit')" v-model="limit" />

        <date-range-picker v-model="timeRange"/>
      </div>


      <template v-slot:controls="{ options }">
        <v-btn :depressed="!options.mobile" color="primary" :disabled="loading" @click="loadData()">
          <responsive-icon :label="$t('shared.refresh')" icon="redo-alt" />
        </v-btn>

        <v-btn :depressed="!options.mobile" color="primary" :disabled="loading" @click="exportToCSV()">
          <responsive-icon :label="$t('csv-export')" icon="file-csv" />
        </v-btn>
      </template>
    </ob-filters>

    <loader v-if="loading"></loader>
    <template v-else>
      <no-data v-if="!Object.keys(data).length">{{ $t('logs.no-assets-selected') }}</no-data>
      <template v-else-if="Object.keys(data).length">
        <v-card v-for="assetId in Object.keys(data)" :key="assetId">
          <h2 class="ml-3 mb-1">{{getAsset(assetId).name}}</h2>
          <div v-for="variableName in Object.keys(data[assetId])" :key="variableName">
            <h4 class="ml-3 mb-1">{{translateVariableName(variableName)}}</h4>

            <v-simple-table v-if="limit || !['analog', 'array'].includes(getVariableConfig(assetId, variableName).type)" class="mb-5">
              <template v-slot:default>
                <thead>
                <tr>
                  <th class="text-left value-column" v-t="'value'"/>
                  <th class="text-left" v-t="'device-statistics.occurrences'"/>
                  <th class="text-left time-column" v-t="'time'"/>
                  <th class="text-left" v-t="'device-statistics.timePct'"/>
                </tr>
                </thead>
                <tbody>
                <tr v-for="([value, stats], index) in data[assetId][variableName].values" :key="index">
                  <td>{{getVariableValueLabel(getAsset(assetId), variableName, value)}}</td>
                  <td>{{stats.occurrences}}</td>
                  <td>{{duration(stats.sumTime).format()}}</td>
                  <td>{{Math.round(stats.percentTime * 1000) / 10}}</td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>

            <div v-else>
              <device-statistics-analogue-panel :data="data[assetId][variableName].values"/>
            </div>
          </div>
        </v-card>
      </template>
    </template>
  </div>

</template>

<style scoped>
.picker-wrapper {
  min-width: 340px !important;
}

@media all and (min-width: 960px) {
  .value-column {
    width: 35%;
  }

  .time-column {
    width: 25%;
  }
}
</style>

<script>
import moment from 'moment'
import DateRangePicker from '../shared/components/date-range-picker.vue'
import DeviceStatisticsAnaloguePanel, { round } from './device-statistics-analogue-panel.vue'
import { CSVExport } from './device-data-export.es6'
import { translateVariableName } from '../helpers/translation'
import AssetsTree from '../assets/components/assets-tree'
import VariablesTree from '../assets/components/variables-tree'
import NoData from '../shared/no-data'
import Loader from '../shared/loader'
import ObFilters from '../shared/components/filters'
import ResponsiveIcon from '../shared/components/responsive-icon'

const sec = 1000
const min = 60 * sec
const defaultTimeIntervalWidth = 60 * min
const variableSeparator = '-'

export default {
  components: {
    VariablesTree,
    ObFilters,
    NoData,
    Loader,
    ResponsiveIcon,
    AssetsTree,
    DateRangePicker,
    DeviceStatisticsAnaloguePanel
  },
  data () {
    const { assetIds = [], variableIds = [], timeRange } = this.$route.params
    return {
      now: null,
      assets: [],
      assetIds,
      variableIds,
      leafVariableIds: [],
      limit: null,
      timeRange,
      loading: false,
      data: {},
      exportLoading: false,
    }
  },
  computed: {
    variableConfigs () {
      const { deviceTypes } = this.$store.state
      const entries = this.leafVariableIds
        .map(id => {
          const [deviceTypeName, variable] = id.split(variableSeparator)
          const deviceType = deviceTypes.find(({ name }) => name === deviceTypeName)
          return [id, deviceType.variables[variable]]
        })
      return Object.fromEntries(entries)
    },
    defaultTimeFrom () {
      return this.now - defaultTimeIntervalWidth
    },
    defaultTimeTo () {
      return this.now
    },
    timeFrom () {
      return this.timeRange.from
    },
    timeTo () {
      return this.timeRange.to
    },
    readyToLoad () {
      return this.leafVariableIds.length !== 0
    }
  },
  watch: {
    assetIds: {
      handler (ids) {
        this.variableIds = []
        this.data = {}
      },
      deep: true
    },
    variableIds: {
      handler () {
        this.data = {}
      },
      deep: true
    },
    timeRange () {
      this.data = {}
    },
    limit () {
      this.data = {}
    }
  },
  async mounted () {
    await this.setNowFromServerTime()
    if (!this.timeRange) {
      this.timeRange = {
        from: this.defaultTimeFrom,
        to: this.defaultTimeTo
      }
    }
  },
  methods: {
    duration: moment.duration,
    translateVariableName,
    async setNowFromServerTime () {
      try {
        this.now = await this.$store.dispatch('getServerTime')
      } catch (error) {
        console.error('Failed to fetch server time:', error.message)
        return new Date().valueOf()
      }
    },
    async loadData () {
      try {
        this.loading = true
        this.$store.commit('clearDeviceStatisticsReportData')
        const { assetIds, leafVariableIds, limit, timeFrom, timeTo } = this
        const promises = assetIds
          .map(assetId => {
            const asset = this.getAsset(assetId)
            const variables = leafVariableIds
              .filter(leafVariableId => leafVariableId.split(variableSeparator)[0] === asset.deviceType)
              .map(leafVariableId => leafVariableId.split(variableSeparator)[1])
              .join(',')
            return this.$store.dispatch('getDeviceStatisticsReport',
              { assetId, variables, limit, timeFrom, timeTo })
          })
        await Promise.all(promises)
      } finally {
        this.data = this.$store.state.deviceStatisticsReportData
        this.loading = false
      }
    },
    getAsset (assetId) {
      return this.assets.find(x => x.id === assetId)
    },
    getVariableValueLabel (asset, variableName, value) {
      const { deviceTypes } = this.$store.state
      const deviceType = deviceTypes.find(({ name }) => name === asset.deviceType)
      const { config } = deviceType.variables[variableName]
      if (config[value]) {
        const code = config[value].code
        return this.$t(`variable-label.${code}`)
      }
      return round(value)
    },
    getVariableConfig (assetId, variableName) {
      const variableId = this.getAsset(assetId).deviceType + variableSeparator + variableName
      return this.variableConfigs[variableId]
    },
    async exportToCSV () {
      try {
        this.exportLoading = true

        // download logs
        const { from, to } = this.timeRange
        const promises = this.assetIds.map((assetId) => {
          return this.$store.dispatch('getLogsExport', {
            assetId,
            timeFrom: from,
            timeTo: to
          })
        })

        // prepare data
        const variableSeparator = '-'

        const responses = await Promise.all(promises)
        const variables = responses.map((data) => {
          let { deviceTyp, fullPath } = data
          let selectedVariables = this.variableIds
            .filter((id) => new RegExp(`^${deviceTyp}${variableSeparator}`).test(id))
            .map((id) => id.split(variableSeparator).pop())

          let variables
          if (selectedVariables.length) {
            variables = selectedVariables.reduce((variables, id) => {
              if (data.variables[id]) {
                variables[id] = data.variables[id]
              }
              return variables
            }, {})
          } else {
            variables = data.variables
          }

          variables.fullPath = fullPath

          return variables
        })

        // create export
        const csv = new CSVExport({
          variables,
          columns: ['fullPath']
        }).export()

        // download CSV file
        const html = window.document
        const file = `logs-export-${new Date().toISOString()}.csv`
        const link = html.createElement('a')
        const href = window.URL.createObjectURL(new Blob([csv]))
        link.setAttribute('href', href)
        link.setAttribute('download', file)
        html.body.appendChild(link)
        link.click()
        html.body.removeChild(link)
      } catch (error) {
        console.error(error)
        this.$store.commit('showSnackbar', {
          message: this.$t('server-error'),
          timeout: 0
        })
      } finally {
        this.exportLoading = false
      }
    }
  }
}
</script>
